import { useCallback } from "react";

/** Hook wrapping fetch API reduces boilerplate code for fetch requests
 * @returns An object with get, post, put, and deleteRequest functions
 * @example
 * const { get, post, put, deleteRequest } = useFetchWithJwt();
 *
 * const response = await get("/api/endpoint");
 * OR
 * const response = await post("/api/endpoint", body);
 *  do not use JSON.stringify on body object, as it is done in this hook
 * */
export default function useFetch() {
    /**  Sends a get request
     * @param url The URL string
     * @param options Optional options object
     * @returns A promise
     */
    const get = useCallback((url: string, options: object = {}) => {
        return fetch(url, {
            ...options,
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
    }, []);

    /**  Sends a post request
     * @param url The URL string
     * @param body The body object (optional). Do not use JSON.stringify() since it happens in post.
     * @param options Options object (optional  )
     * @returns A promise
     * */
    const post = useCallback(
        (url: string, body: object = {}, options: object = {}) => {
            return fetch(url, {
                ...options,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
        },
        []
    );

    /** Sends a put request */
    const put = useCallback(
        (url: string, body: object = {}, options: object = {}) => {
            return fetch(url, {
                ...options,
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
        },
        []
    );

    /**  Sends a delete request (delete is a reserved name) */
    const deleteRequest = useCallback((url: string, options: object = {}) => {
        return fetch(url, {
            ...options,
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        });
    }, []);

    return { get, post, put, deleteRequest };
}
